import {
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  BorderStyle,
  AlignmentType,
} from 'docx';
import { normalizeSource } from '../../utils/normalizeSource';

const NONE = { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' };
const NO_BORDERS = { top: NONE, bottom: NONE, left: NONE, right: NONE };
const TABLE_NO_BORDERS = { ...NO_BORDERS, insideH: NONE, insideV: NONE };

export function renderPropertyInfoDocx({ block, children, rakennusData, mergedRakennusData }) {
  const data =
    mergedRakennusData && Object.keys(mergedRakennusData).length > 0
      ? mergedRakennusData
      : rakennusData;

  const rakennukset = data?.rakennukset_fulls || [];
  if (!rakennukset.length) {
    children.push(
      new Paragraph({
        children: [new TextRun({ text: 'Ei kiinteistötietoja saatavilla.', size: 22 })],
        spacing: { after: 120 },
      })
    );
    return;
  }

  const rak = rakennukset[0];
  const mainBuildings = rakennukset.filter(r => r.isMainBuilding);
  const firstMain = mainBuildings[0] || {};

  const sumField = field => mainBuildings.reduce((s, r) => s + (parseFloat(r[field]) || 0), 0);
  const sumKokonaisala = sumField('kokonaisala');
  const sumKerrosala = sumField('kerrosala');
  const sumHuoneistoala = sumField('huoneistoala');
  const sumTilavuus = sumField('tilavuus');

  const kerrokset = mainBuildings.map(r => r.kerroksia ?? 0);
  let sumKerroksia = '—';
  if (kerrokset.length) {
    const [mn, mx] = [Math.min(...kerrokset), Math.max(...kerrokset)];
    sumKerroksia = mn === mx ? `${mn}` : `${mn}–${mx}`;
    if (sumKerroksia === '0–0') sumKerroksia = '0';
  }

  const kellarit = mainBuildings.map(r => r.kellarikerroksia ?? 0);
  let sumKellarikerroksia = '—';
  if (kellarit.length) {
    const [mn, mx] = [Math.min(...kellarit), Math.max(...kellarit)];
    sumKellarikerroksia = mn === mx ? `${mx}` : `${mn}–${mx}`;
    if (sumKellarikerroksia === '0–0') sumKellarikerroksia = '0';
  }

  const allAddresses = rakennukset
    .map(r => r.osoite?.trim())
    .filter(Boolean)
    .filter((v, i, arr) => arr.indexOf(v) === i);

  const infoSections = [
    {
      rows: [
        ['Kohteen nimi', data.kohteen_nimi || '—', data.metadata?.kohteen_nimi?.source || ''],
        ['Osoite', allAddresses.join(', ') || '—', rakennukset[0]?.metadata?.osoite?.source || ''],
        ['Toimipaikka', `${rak.postinumero || ''} ${rak.toimipaikka || ''}`.trim() || '—', rak.metadata?.toimipaikka?.source || ''],
      ],
    },
    {
      rows: [
        ['Kiinteistötunnus', (data.kiinteistotunnus || '—').replace(/\n/g, ', '), data.metadata?.kiinteistotunnus?.source || ''],
        ['Tontin koko', data.tontin_koko ? `${Math.round(data.tontin_koko)} m²` : '—', data.metadata?.tontin_koko?.source || ''],
        ['Tontin omistaja', data.tontin_omistaja || '—', data.metadata?.tontin_omistaja?.source || ''],
      ],
    },
    {
      rows: [
        ['Rakennusten lukumäärä', String(data.rakennusten_lkm || '—'), data.metadata?.rakennusten_lkm?.source || ''],
        ['Ulkorakennusten lukumäärä', String(data.ulkorakennusten_lkm || '—'), data.metadata?.ulkorakennusten_lkm?.source || ''],
        ['Huoneistojen lukumäärä', String(data.huoneistojen_lkm || '—'), data.metadata?.huoneistojen_lkm?.source || ''],
      ],
    },
    {
      rows: [
        ['Rakennusvuosi', String(rak.rakennusvuosi || '—'), rak.metadata?.rakennusvuosi?.source || ''],
        ['Peruskorjausvuosi', String(rak.peruskorjausvuosi || '—'), rak.metadata?.peruskorjausvuosi?.source || ''],
        ['Laajennusvuosi', String(rak.laajennusvuosi || '—'), rak.metadata?.laajennusvuosi?.source || ''],
      ],
    },
    {
      rows: [
        ['Kokonaisala', sumKokonaisala ? `${sumKokonaisala} m²` : '—', firstMain.metadata?.kokonaisala?.source || ''],
        ['Kerrosala', sumKerrosala ? `${sumKerrosala} m²` : '—', firstMain.metadata?.kerrosala?.source || ''],
        ['Huoneistoala', sumHuoneistoala ? `${sumHuoneistoala} m²` : '—', firstMain.metadata?.huoneistoala?.source || ''],
        ['Tilavuus', sumTilavuus ? `${sumTilavuus} m³` : '—', firstMain.metadata?.tilavuus?.source || ''],
        ['Kerroksia', sumKerroksia, firstMain.metadata?.kerroksia?.source || ''],
        ['Kellarikerroksia', sumKellarikerroksia, firstMain.metadata?.kellarikerroksia?.source || ''],
      ],
    },
  ];

  infoSections.forEach(sec => {
    const visibleRows = sec.rows.filter(([, value]) => {
      const v = (value ?? '').toString().trim();
      return v && v !== '—' && v !== '-';
    });
    if (!visibleRows.length) return;

    // Green separator line
    children.push(
      new Paragraph({
        children: [],
        border: { bottom: { style: BorderStyle.SINGLE, size: 8, color: '198754' } },
        spacing: { before: 200, after: 100 },
      })
    );

    const tableRows = visibleRows.map(
      ([label, value, source]) =>
        new TableRow({
          children: [
            new TableCell({
              children: [
                new Paragraph({
                  children: [new TextRun({ text: label, bold: true, size: 22 })],
                  spacing: { after: 40 },
                }),
              ],
              width: { size: 30, type: WidthType.PERCENTAGE },
              borders: NO_BORDERS,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [new TextRun({ text: value, size: 22 })],
                  spacing: { after: 40 },
                }),
              ],
              width: { size: 40, type: WidthType.PERCENTAGE },
              borders: NO_BORDERS,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({ text: normalizeSource(source) || '', italics: true, color: '666666', size: 20 }),
                  ],
                  alignment: AlignmentType.RIGHT,
                  spacing: { after: 40 },
                }),
              ],
              width: { size: 30, type: WidthType.PERCENTAGE },
              borders: NO_BORDERS,
            }),
          ],
        })
    );

    children.push(
      new Table({
        rows: tableRows,
        width: { size: 100, type: WidthType.PERCENTAGE },
        borders: TABLE_NO_BORDERS,
      })
    );
  });
}
